/**
 * Enhanced Support Chat with AI Assistant
 * Estate Flow - Sebastian Alliance Group LLC
 * 
 * Comprehensive help for all user types including Vacation Rental Managers
 */
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, MessageCircle, Send, Minimize2, Wrench, BookOpen, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Comprehensive Knowledge Base Data
const KNOWLEDGE_BASE = {
  // Vacation Rental Manager Topics
  vacation_rental: {
    getting_started: {
      title: "Getting Started as a Vacation Rental Manager",
      content: `Welcome to Estate Flow! Here's how to get started:

**Step 1: Sign Up**
• Go to /vacation-rentals/search
• Click "List Your Property"
• Fill out your information
• Select "Vacation Rental Manager"

**Step 2: Complete Onboarding**
• Enter your business details
• Add your bank account for payouts
• Review pricing ($99 setup, then monthly based on properties)
• Complete payment via Stripe

**Step 3: Access Your Dashboard**
• Login at /vacation-rental/login
• You'll see your Vacation Rentals Management dashboard

**Step 4: Add Properties**
• Click the "Properties" tab
• Click "Add Property"
• Fill in property details (name, location, bedrooms, etc.)
• Set your nightly rate and cleaning fee
• Toggle "List Publicly" to show on search`
    },
    add_property: {
      title: "How to Add a Vacation Rental Property",
      content: `**Adding Your First Property:**

1. Login to your dashboard at /vacation-rental/login
2. Click the "Properties" tab
3. Click the green "Add Property" button
4. Fill in the details:
   • Property Name (e.g., "Beach Paradise Villa")
   • Property Type (Villa, Condo, Apartment, House, Cottage, Beachfront)
   • Description
   • Location (City, Country, Address)
   • Bedrooms, Bathrooms, Max Guests
   • Base Nightly Rate
   • Cleaning Fee
   • Amenities (WiFi, Parking, Kitchen, Pool, Beach access)
   • Direct Booking Discount (% off for direct bookings)

5. Click "Add Property" to save

**Making Property Public:**
• Toggle "List Publicly" to show your property on the public search page
• Your property will appear at /vacation-rentals/search`
    },
    bookings: {
      title: "Managing Bookings",
      content: `**Viewing Bookings:**
• Go to the "Bookings" tab in your dashboard
• See all upcoming, current, and past bookings
• View guest details, dates, and payment status

**Booking Sources:**
• Book Direct - Guests book directly on your listing (you keep 96%!)
• Expedia - Synced via Channel Manager
• Booking.com - Synced via Channel Manager
• VRBO - Synced via Channel Manager
• Airbnb - Synced via Channel Manager

**Confirming Bookings:**
• New bookings appear as "Pending"
• Review guest details
• Click "Confirm" to accept
• Guest receives confirmation email`
    },
    channel_manager: {
      title: "Channel Manager - Connect to OTAs",
      content: `**What is Channel Manager?**
Sync your properties across multiple booking platforms (OTAs) from one dashboard.

**Supported Channels:**
• Expedia
• Booking.com
• VRBO
• Airbnb

**How to Connect:**
1. Go to "Channel Manager" tab
2. Click "Connect" next to desired channel
3. Enter your credentials for that platform
4. Authorize the connection
5. Select which properties to sync

**Benefits:**
• Automatic calendar sync (no double bookings!)
• Unified inbox for all messages
• Centralized pricing management
• One dashboard for everything`
    },
    pricing: {
      title: "Vacation Rental Pricing",
      content: `**Estate Flow Pricing for Vacation Rentals:**

**Setup Fee:** $99 (one-time)

**Monthly Fee (based on properties):**
• 1-3 properties: $19/month
• 4-10 properties: $69/month
• 11-25 properties: $199/month
• 26-50 properties: $599/month

**Transaction Fee:** 4% per booking

**Compare to OTAs:**
• Airbnb charges 15-20% per booking
• Booking.com charges 15-25% per booking
• With Estate Flow direct bookings, you keep 96%!

**Example Savings:**
$500 booking on Airbnb → You get $400-425
$500 booking on Estate Flow → You get $480`
    },
    payouts: {
      title: "Receiving Payouts",
      content: `**Payout Methods:**
• Bank Transfer (ACH) - 3-5 business days
• Visa Direct - Within minutes (real-time!)
• PayPal - 1-2 business days
• Zelle - Same day

**Setting Up Payouts:**
1. Go to "Payouts" tab in dashboard
2. Click "Payout Settings"
3. Select your preferred method
4. Enter account details
5. Save settings

**Requesting Payouts:**
• Minimum balance: $50
• Click "Request Payout" button
• Select amount
• Confirm request
• Track status in "Payout History"`
    },
    book_direct_widget: {
      title: "Book Direct Widget",
      content: `**What is Book Direct Widget?**
A booking widget you can embed on your own website to accept direct bookings.

**Benefits:**
• Skip OTA fees entirely
• Keep 96% of every booking
• Your branding, your customers
• Direct relationship with guests

**How to Get Your Widget:**
1. Go to "Book Direct Widget" tab
2. Select a property
3. Copy the embed code
4. Paste into your website HTML

**Customization:**
• Widget matches your property colors
• Shows real-time availability
• Secure payment processing
• Instant booking confirmation`
    }
  },
  // Landlord Topics
  landlord: {
    getting_started: {
      title: "Getting Started as a Landlord",
      content: `**Welcome to Estate Flow!**

1. Sign up at /signup
2. Select "Landlord" as your role
3. Complete onboarding
4. Add your properties
5. Invite tenants
6. Start collecting rent automatically!

**Dashboard Features:**
• Property management
• Tenant portal
• Rent collection
• Maintenance requests
• Financial reports`
    },
    add_tenant: {
      title: "How to Add Tenants",
      content: `**Adding a Tenant:**

1. Login to your landlord dashboard
2. Go to "Properties" section
3. Select the property
4. Click "Add Tenant" or "Invite Tenant"
5. Enter tenant's email address
6. Set lease details (start date, rent amount)
7. Click "Send Invitation"

The tenant will receive an email to:
• Create their account
• Access tenant portal
• Set up payment method
• Pay rent online`
    }
  },
  // HOA Topics
  hoa: {
    getting_started: {
      title: "Getting Started with HOA Management",
      content: `**For HOA Administrators:**

1. Sign up at /signup
2. Select "HOA Administrator"
3. Complete onboarding with community details
4. Add units/properties
5. Invite homeowners
6. Manage dues and assessments

**For HOA Owners:**
• Login at /hoa-owner/login
• Pay dues online
• Submit maintenance requests
• View community updates`
    }
  },
  // General Topics
  general: {
    login_help: {
      title: "Login Help",
      content: `**Login Pages by Account Type:**

• Vacation Rental Managers: /vacation-rental/login
• Landlords: /landlord/login
• Property Managers: /property-manager/login
• Tenants: /tenant/login
• HOA Administrators: /hoa-admin/login
• HOA Owners: /hoa-owner/login

**Forgot Password?**
1. Go to your login page
2. Click "Forgot Password?"
3. Enter your email
4. Check your inbox for reset link
5. Create new password

**Still Can't Login?**
• Check email spelling
• Make sure caps lock is off
• Try resetting password
• Contact support@apcestateflow.com`
    },
    payment_issues: {
      title: "Payment Issues",
      content: `**Common Payment Issues:**

**Card Declined:**
• Check card number is correct
• Ensure sufficient funds
• Verify expiration date
• Try a different card

**Payment Not Processing:**
• Refresh the page
• Clear browser cache
• Try a different browser
• Contact your bank

**Refund Requests:**
• Contact support@apcestateflow.com
• Include booking/transaction ID
• Allow 5-10 business days

**For Immediate Help:**
📧 support@apcestateflow.com`
    },
    referral_program: {
      title: "Referral Program - Earn 25%",
      content: `**How the Referral Program Works:**

**Earn 25% Recurring Commission!**
• Get paid every month your referrals stay active
• Commission lasts for the lifetime of the account
• No limits on how many you can refer

**How to Start:**
1. Login to your dashboard
2. Go to "Referrals" section
3. Copy your unique referral link
4. Share with landlords, property managers, HOAs
5. Track earnings in real-time

**Example Earnings:**
• Refer 10 landlords paying $99/month = $247.50/month
• Refer 20 landlords = $495/month
• Refer 50 landlords = $1,237.50/month

**Payout Options:**
• Bank Transfer (ACH)
• PayPal
• Zelle
• Visa Direct (instant)`
    }
  }
};

const SupportChat = () => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showQuickActions, setShowQuickActions] = useState(true);
  const messagesEndRef = useRef(null);
  
  useEffect(() => {
    setMessages([{
      role: 'assistant',
      content: "👋 Hi! I'm your Estate Flow Assistant!\n\nI can help you with:\n• Setting up your account\n• Adding properties\n• Managing bookings\n• Payment questions\n• Navigation help\n\n**Quick Actions:**",
      timestamp: new Date(),
      showActions: true
    }]);
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const quickActions = [
    { label: "🏠 Add a Property", query: "How do I add a property?" },
    { label: "🔑 Login Help", query: "I need help logging in" },
    { label: "💰 Pricing Info", query: "What are the prices?" },
    { label: "📅 Manage Bookings", query: "How do I manage bookings?" },
    { label: "💳 Payout Setup", query: "How do I receive payouts?" },
    { label: "📚 Knowledge Base", action: () => navigate('/kb') }
  ];

  const handleQuickAction = (action) => {
    if (action.action) {
      action.action();
    } else if (action.query) {
      setInputMessage(action.query);
      setTimeout(() => handleSendMessage(action.query), 100);
    }
    setShowQuickActions(false);
  };

  const getAIResponse = (question) => {
    const q = question.toLowerCase();
    
    // ==========================================
    // TENANT SPECIFIC QUESTIONS
    // ==========================================
    if (q.includes('tenant') && (q.includes('i am') || q.includes("i'm") || q.includes('as a'))) {
      return `👤 **Tenant Help**

Welcome! As a tenant, here's what you can do:

**Login:** /tenant/login

**Your Portal Features:**
• **Pay Rent** - Make monthly payments online
• **Payment History** - View all past payments
• **Maintenance** - Submit repair requests
• **Documents** - Access lease and other documents
• **Messages** - Communicate with landlord

**Common Questions:**
• "How do I pay rent?"
• "How do I submit maintenance request?"
• "Where can I see my lease?"

📚 Visit /kb and select "Tenants" for detailed guides`;
    }

    // Tenant paying rent
    if ((q.includes('tenant') || q.includes('rent')) && (q.includes('pay') || q.includes('payment'))) {
      return `💳 **Paying Rent as a Tenant**

**How to Pay Rent:**
1. Login at **/tenant/login**
2. Click **"Pay Rent"** button
3. Confirm amount
4. Select payment method
5. Submit payment
6. Receive confirmation

**Payment Methods:**
• Credit/Debit Card
• Bank Account (ACH)
• Apple Pay/Google Pay

**Auto-Pay Setup:**
1. Go to "Payment Settings"
2. Enable "Auto-Pay"
3. Select payment method
4. Payments made automatically!

📚 Visit /kb for more tenant help`;
    }

    // ==========================================
    // LANDLORD SPECIFIC QUESTIONS
    // ==========================================
    if (q.includes('landlord') && (q.includes('i am') || q.includes("i'm") || q.includes('as a') || q.includes('getting started'))) {
      return `🏠 **Landlord Help**

Welcome! Here's how to get started as a landlord:

**Sign Up:** /signup → Select "Landlord"

**Login:** /landlord/login

**Dashboard Features:**
• **Properties** - Add and manage rental properties
• **Tenants** - Invite and manage tenants
• **Rent Collection** - Automatic rent collection
• **Maintenance** - Handle repair requests
• **Reports** - Financial reporting & P&L

**Pricing:**
• Setup: $162 base + $81/additional building
• Monthly: $161 (1-5 units) to $755 (51+ units)

**First Steps:**
1. Add your properties
2. Add units/apartments
3. Set rent amounts
4. Invite tenants
5. Start collecting rent!

📚 Visit /kb and select "Landlords" for detailed guides`;
    }

    // Landlord adding tenants
    if ((q.includes('landlord') || q.includes('tenant')) && (q.includes('add') || q.includes('invite'))) {
      return `👥 **Adding Tenants (For Landlords)**

**How to Add a Tenant:**
1. Login at **/landlord/login**
2. Go to **"Properties"** section
3. Select the property
4. Click the unit to assign
5. Click **"Add Tenant"** or **"Invite Tenant"**
6. Enter:
   • Tenant's email
   • Lease start/end dates
   • Monthly rent amount
   • Security deposit
7. Click **"Send Invitation"**

**What Happens Next:**
Tenant receives email to:
• Create their account
• Set up payment method
• Access tenant portal
• Pay rent online

📚 Visit /kb for more landlord guides`;
    }

    // ==========================================
    // PROPERTY MANAGER SPECIFIC QUESTIONS  
    // ==========================================
    if (q.includes('property manager') || q.includes('management company')) {
      return `🏢 **Property Manager Help**

For professional property managers handling multiple properties.

**Sign Up:** /signup → Select "Property Manager"

**Login:** /property-manager/login

**Pricing:**
• Setup: $1,499 (one-time)
• Monthly: $215
• Transaction Fee: 2%

**Features:**
• **Multi-property management** - Unlimited properties
• **Client portal** - For property owners
• **Tenant management** - Full lifecycle
• **Rent collection** - Automated
• **Maintenance tracking** - Work orders
• **Financial reporting** - Owner statements
• **Team management** - Add staff with roles

**Team Roles:**
• Admin - Full access
• Accountant - Financial only
• Maintenance - Repairs only
• Leasing - Tenant management

📚 Visit /kb and select "Property Managers" for guides`;
    }

    // ==========================================
    // HOA SPECIFIC QUESTIONS
    // ==========================================
    if (q.includes('hoa') && (q.includes('admin') || q.includes('administrator') || q.includes('manage'))) {
      return `🏘️ **HOA Administrator Help**

**Sign Up:** /signup → Select "HOA Administrator"

**Login:** /hoa-admin/login

**Pricing:**
• Setup: $323 base + $120/additional building
• Monthly: $269 (1-10 units) to $1,619 (101+ units)

**Dashboard Features:**
• **Units** - Manage all community units
• **Owners** - Homeowner directory
• **Dues** - Collect HOA dues
• **Assessments** - Special assessments
• **Maintenance** - Common area repairs
• **Documents** - CC&Rs, bylaws
• **Communications** - Announcements

**First Steps:**
1. Add your community details
2. Set up dues structure
3. Add units
4. Invite homeowners
5. Start collecting dues!

📚 Visit /kb and select "HOA Admin" for guides`;
    }

    if (q.includes('hoa') && (q.includes('owner') || q.includes('homeowner') || q.includes('dues') || q.includes('pay'))) {
      return `🏡 **HOA Owner Help**

**Login:** /hoa-owner/login

**Your Portal Features:**
• **Pay Dues** - Make HOA payments online
• **Payment History** - View all past payments
• **Assessments** - View special assessments
• **Maintenance** - Submit requests for common areas
• **Documents** - Access CC&Rs, bylaws
• **Announcements** - Community updates

**How to Pay HOA Dues:**
1. Login at /hoa-owner/login
2. Click **"Pay Dues"**
3. Confirm amount
4. Select payment method
5. Submit payment

**Auto-Pay:** Enable automatic payments to never miss a due date!

📚 Visit /kb and select "HOA Owners" for guides`;
    }

    // ==========================================
    // REFERRAL PARTNER QUESTIONS
    // ==========================================
    if (q.includes('referral') || q.includes('commission') || q.includes('affiliate') || q.includes('partner program')) {
      return `💰 **Referral Partner Program**

Earn **25% recurring commission** by referring users!

**Login:** /referral/login

**How It Works:**
1. Sign up as referral partner
2. Get your unique referral link
3. Share with landlords, PMs, HOAs
4. Earn 25% of their subscription FOREVER!

**Example Earnings:**
• 10 referrals = ~$247/month
• 20 referrals = ~$495/month
• 50 referrals = ~$1,237/month

**Payout Methods:**
• Bank Transfer (ACH)
• PayPal
• Zelle
• Visa Direct (instant!)

**Minimum payout:** $50

📚 Visit /kb and select "Referral Partners" for guides`;
    }

    // ==========================================
    // VACATION RENTAL SPECIFIC QUESTIONS
    // ==========================================
    if (q.includes('vacation') || q.includes('rental property') || q.includes('list property') || q.includes('vacation rental')) {
      if (q.includes('start') || q.includes('begin') || q.includes('setup') || q.includes('new') || q.includes('sign up')) {
        return KNOWLEDGE_BASE.vacation_rental.getting_started.content + "\n\n📚 **Need more help?** Visit /kb";
      }
      if (q.includes('add') || q.includes('create') || q.includes('property')) {
        return KNOWLEDGE_BASE.vacation_rental.add_property.content + "\n\n📚 **Need more help?** Visit /kb";
      }
      if (q.includes('booking') || q.includes('reservation')) {
        return KNOWLEDGE_BASE.vacation_rental.bookings.content;
      }
      if (q.includes('channel') || q.includes('airbnb') || q.includes('expedia') || q.includes('vrbo') || q.includes('booking.com')) {
        return KNOWLEDGE_BASE.vacation_rental.channel_manager.content;
      }
      if (q.includes('price') || q.includes('cost') || q.includes('fee')) {
        return KNOWLEDGE_BASE.vacation_rental.pricing.content;
      }
      if (q.includes('payout') || q.includes('payment') || q.includes('money') || q.includes('paid')) {
        return KNOWLEDGE_BASE.vacation_rental.payouts.content;
      }
      if (q.includes('widget') || q.includes('embed') || q.includes('direct')) {
        return KNOWLEDGE_BASE.vacation_rental.book_direct_widget.content;
      }
      return `🏖️ **Vacation Rental Manager Help**

**Sign Up:** /signup → Select "Vacation Rental Manager"
Or click "List Your Property" on /vacation-rentals/search

**Login:** /vacation-rental/login

**Pricing:**
• Setup: $99 (one-time)
• Monthly: $19-$599 based on properties
• Transaction Fee: 4%

**Dashboard Features:**
• **Properties** - Add and manage rentals
• **Bookings** - View reservations
• **Channel Manager** - Connect to Airbnb, Expedia, VRBO
• **Book Direct Widget** - Accept bookings on your website
• **Payouts** - Manage earnings

**What would you like help with?**
• "How do I add a property?"
• "How do bookings work?"
• "How do I connect to Airbnb?"

📚 Visit /kb for detailed guides`;
    }

    // ==========================================
    // GENERAL QUESTIONS
    // ==========================================
    
    // Property questions (general)
    if (q.includes('add property') || q.includes('create property') || q.includes('new property') || (q.includes('property') && (q.includes('how') || q.includes('add')))) {
      return `🏠 **Adding a Property**

The steps depend on your account type:

**Vacation Rental Managers:**
Login at /vacation-rental/login → Properties tab → Add Property

**Landlords:**
Login at /landlord/login → Add Property button

**Property Managers:**
Login at /property-manager/login → Properties → Add

**What type of account do you have?**
• "I'm a vacation rental manager"
• "I'm a landlord"
• "I'm a property manager"

📚 Visit /kb for step-by-step guides`;
    }

    // Login questions
    if (q.includes('login') || q.includes('sign in') || q.includes('password') || q.includes('access') || q.includes("can't get in")) {
      return KNOWLEDGE_BASE.general.login_help.content;
    }

    // Payment questions
    if (q.includes('payout') || q.includes('payment') || q.includes('money') || q.includes('pay') || q.includes('bank')) {
      return `💳 **Payment Help**

**For Tenants (paying rent):**
Login at /tenant/login → Pay Rent

**For HOA Owners (paying dues):**
Login at /hoa-owner/login → Pay Dues

**For Landlords (receiving rent):**
Funds deposited automatically to your bank

**For Vacation Rental Managers (payouts):**
Dashboard → Payouts tab → Request Payout

**Payout Methods:**
• Bank Transfer (ACH) - 3-5 days
• PayPal - 1-2 days
• Zelle - Same day
• Visa Direct - Instant!

📚 Visit /kb for detailed payment guides`;
    }

    // Maintenance questions
    if (q.includes('maintenance') || q.includes('repair') || q.includes('fix') || q.includes('broken')) {
      return `🔧 **Maintenance Requests**

**For Tenants:**
1. Login at /tenant/login
2. Click "Maintenance"
3. Click "New Request"
4. Describe the issue
5. Upload photos
6. Submit

**For Landlords (managing requests):**
• View requests in dashboard
• Update status
• Communicate with tenant

**For HOA Owners:**
Submit requests for common areas through your portal

📚 Visit /kb for more maintenance help`;
    }

    // Price questions
    if (q.includes('price') || q.includes('cost') || q.includes('fee') || q.includes('how much')) {
      return `💎 **Pricing Overview**

**Landlords:**
• Setup: $162 + $81/building
• Monthly: $161-$755

**HOA Communities:**
• Setup: $323 + $120/building
• Monthly: $269-$1,619

**Vacation Rentals:**
• Setup: $99
• Monthly: $19-$599
• Transaction: 4%

**Property Managers:**
• Setup: $1,499
• Monthly: $215
• Transaction: 2%

All plans include 10-day free trial!

📚 Visit /kb for detailed pricing`;
    }

    // Getting started / general help
    if (q.includes('start') || q.includes('help') || q.includes('how') || q.includes('what') || q.includes('navigate')) {
      return `👋 **Welcome to Estate Flow!**

I can help you with all account types:

**🏖️ Vacation Rental Managers**
Login: /vacation-rental/login
"Help me as vacation rental manager"

**🏠 Landlords**
Login: /landlord/login
"Help me as a landlord"

**👤 Tenants**
Login: /tenant/login
"Help me as a tenant"

**🏢 Property Managers**
Login: /property-manager/login
"Help me as property manager"

**🏘️ HOA Administrators**
Login: /hoa-admin/login
"Help me with HOA management"

**🏡 HOA Owners**
Login: /hoa-owner/login
"Help me pay HOA dues"

**💰 Referral Partners**
Login: /referral/login
"Tell me about referral program"

📚 **Knowledge Base:** /kb
📧 **Email:** support@apcestateflow.com

What type of account do you have?`;
    }

    // Default response
    return `I'm here to help! 😊

**I can assist all user types:**

🏖️ **Vacation Rental Managers** - "vacation rental help"
🏠 **Landlords** - "landlord help"
👤 **Tenants** - "tenant help"
🏢 **Property Managers** - "property manager help"
🏘️ **HOA Admins** - "HOA admin help"
🏡 **HOA Owners** - "HOA owner help"
💰 **Referral Partners** - "referral program"

**Common Questions:**
• "How do I add a property?"
• "How do I pay rent?"
• "How do I login?"
• "What are the prices?"

📚 **Knowledge Base:** Visit /kb for detailed guides
📧 **Email:** support@apcestateflow.com

What can I help you with?`;
  };

  const handleSendMessage = async (directMessage = null) => {
    const messageToSend = directMessage || inputMessage;
    if (!messageToSend.trim() || isLoading) return;

    const userMessage = {
      role: 'user',
      content: messageToSend,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);
    setShowQuickActions(false);

    await new Promise(resolve => setTimeout(resolve, 600));

    const response = getAIResponse(messageToSend);
    
    const assistantMessage = {
      role: 'assistant',
      content: response,
      timestamp: new Date()
    };
    
    setMessages(prev => [...prev, assistantMessage]);
    setIsLoading(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  if (!isOpen) {
    return (
      <div className="fixed left-6 bottom-6 z-50">
        <Button
          onClick={() => setIsOpen(true)}
          className="h-14 w-14 rounded-full bg-emerald-600 hover:bg-emerald-700 shadow-lg flex items-center justify-center"
          aria-label="Open support chat"
          data-testid="support-chat-button"
        >
          <MessageCircle className="h-6 w-6 text-white" />
        </Button>
        <div className="absolute -top-2 -right-2 h-4 w-4 bg-red-500 rounded-full animate-pulse"></div>
      </div>
    );
  }

  return (
    <div className="fixed left-6 bottom-6 z-50">
      <Card className={`w-96 shadow-2xl transition-all ${isMinimized ? 'h-16' : 'h-[550px]'} flex flex-col`}>
        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 text-white p-4 rounded-t-lg flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="h-12 w-12 bg-white rounded-full flex items-center justify-center shadow-lg">
              <Wrench className="h-6 w-6 text-emerald-600" />
            </div>
            <div>
              <h3 className="font-bold text-base">Estate Flow Assistant 🔧</h3>
              <p className="text-xs text-emerald-100">Here to help you navigate!</p>
              <p className="text-xs text-emerald-200 flex items-center gap-1">
                <span className="h-2 w-2 bg-green-400 rounded-full"></span>
                Online & Ready
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/kb')}
              className="h-8 w-8 p-0 hover:bg-emerald-800 text-white"
              title="Knowledge Base"
            >
              <BookOpen className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsMinimized(!isMinimized)}
              className="h-8 w-8 p-0 hover:bg-emerald-800 text-white"
            >
              <Minimize2 className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsOpen(false)}
              className="h-8 w-8 p-0 hover:bg-emerald-800 text-white"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {!isMinimized && (
          <>
            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
              {messages.map((msg, index) => (
                <div key={index}>
                  <div className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div
                      className={`max-w-[85%] rounded-lg p-3 ${
                        msg.role === 'user'
                          ? 'bg-emerald-600 text-white'
                          : 'bg-white text-gray-800 border border-gray-200'
                      }`}
                    >
                      <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                      <p className={`text-xs mt-1 ${msg.role === 'user' ? 'text-emerald-100' : 'text-gray-400'}`}>
                        {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                  </div>
                  {/* Quick Actions after first message */}
                  {msg.showActions && showQuickActions && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {quickActions.map((action, i) => (
                        <button
                          key={i}
                          onClick={() => handleQuickAction(action)}
                          className="text-xs px-3 py-1.5 bg-emerald-100 text-emerald-700 rounded-full hover:bg-emerald-200 transition-colors"
                        >
                          {action.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              {isLoading && (
                <div className="flex justify-start">
                  <div className="bg-white border border-gray-200 rounded-lg p-3">
                    <div className="flex space-x-2">
                      <div className="h-2 w-2 bg-emerald-400 rounded-full animate-bounce"></div>
                      <div className="h-2 w-2 bg-emerald-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                      <div className="h-2 w-2 bg-emerald-400 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="p-4 bg-white border-t border-gray-200 rounded-b-lg">
              <div className="flex space-x-2">
                <Input
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Ask me anything..."
                  className="flex-1"
                  disabled={isLoading}
                  data-testid="support-chat-input"
                />
                <Button
                  onClick={() => handleSendMessage()}
                  disabled={!inputMessage.trim() || isLoading}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="support-chat-send"
                >
                  <Send className="h-4 w-4" />
                </Button>
              </div>
              <div className="flex justify-between items-center mt-2">
                <button
                  onClick={() => navigate('/kb')}
                  className="text-xs text-emerald-600 hover:underline flex items-center gap-1"
                >
                  <BookOpen className="h-3 w-3" />
                  Knowledge Base
                </button>
                <span className="text-xs text-gray-400">
                  📧 support@apcestateflow.com
                </span>
              </div>
            </div>
          </>
        )}
      </Card>
    </div>
  );
};

export default SupportChat;
